import React from 'react'
import { graphql } from 'react-apollo'
import { withRouter } from 'react-router-dom'

import { FormRows, SelectInputV3, Spinner } from '@acorns/web-components'
import { FormikProps, withFormik } from 'formik'
import { pathOr } from 'ramda'
import {
  branch,
  compose,
  fromRenderProps,
  renderComponent,
  withHandlers,
  withProps,
} from 'recompose'
import styled from 'styled-components'

import Button from 'components/Button'
import CenteredSpinner from 'components/CenteredSpinner'
import { CTAContainer } from 'components/CTAContainer'
import FormContainer from 'components/FormContainer'
import withHeroText, { HeroTextProps } from 'components/HOCs/withHeroText'
import Layout from 'components/Layout'
import Nav from 'components/Nav'
import Separator from 'components/Separator'
import { withClickTrack } from 'components/Trackers/ClickTracker'
import { TRACKER_IDS } from 'components/Trackers/trackerIDs'
import { ActionType, PageName, User } from 'generated/types'
import { TestIDs } from 'test/utils/testIds'
import { style } from 'theme'
import { withApolloErrorModal } from 'utils/Apollo'
import { withEnv } from 'utils/environment'
import { WithFlowRouterProps, withFlowRouter } from 'utils/FlowProvider'
import { FlowRoute } from 'utils/FlowProvider/types'
import { FunnelContext, FunnelContextProps } from 'utils/FunnelContext'
import { WithOptimizelyProps, withOptimizely } from 'utils/Optimizely'
import { shouldGrayOutCTACall } from 'utils/shouldGrayOutCTA'

import { handleSubmit, mapPropsToValues, mapRowsAndQuestions, validate } from './form'
import openCoreInvestmentAccount from './gql/openCoreInvestmentAccount.mutation.gql'
import suitabilityQuestions from './gql/suitabilityResponses.query.gql'
import updateSuitability from './gql/updateSuitability.mutation.gql'

type Props = {
  data: GqlProps
  handleSelectChange: (field: string, value: string) => void
  onChange: (e: React.SyntheticEvent) => void
  rows: FormRows
} & FormikProps<any> &
  HeroTextProps

type GqlProps = {
  suitabilityResponses?: SuitabilityResponse[]
  user: User
  loading: boolean
}

type PossibleAnswer = {
  text: string
  description: string | null
  summary: string
  id: string
}

export type SuitabilityResponse = {
  answer: {
    text: string
  }
  question: {
    id: string
    possibleAnswers: PossibleAnswer[]
    text: string
  }
}

type InnerProps = Props &
  SuitabilityResponse &
  GqlProps &
  WithFlowRouterProps &
  WithOptimizelyProps &
  FunnelContextProps

const SuitabilityQuestionsButton = withClickTrack<any, any>(
  TRACKER_IDS.SUITABILITY_QUESTIONS.NEXT_BUTTON,
)(Button)

export const enhance = compose<InnerProps, {}>(
  fromRenderProps<Partial<FunnelContextProps>, {}, Partial<FunnelContextProps>>(
    FunnelContext.Consumer,
    ({ isBackEnabled, isDynamicFunnelEnabled, metadata, routeToPage }) => ({
      isBackEnabled,
      isDynamicFunnelEnabled,
      metadata,
      routeToPage,
    }),
  ),
  withEnv,
  withRouter,
  withFlowRouter,
  withOptimizely,
  withHeroText,
  withApolloErrorModal,
  graphql<InnerProps, GqlProps, {}, {}>(suitabilityQuestions, {
    options: { fetchPolicy: 'network-only' },
    props: ({ data: { loading, suitabilityResponses, user } }) => {
      if (!loading) {
        return {
          loading,
          responses: suitabilityResponses,
          portfolioId: pathOr(null, ['portfolio', 'id'], user),
        }
      } else {
        return {
          loading,
          responses: [],
          portfolioId: null,
        }
      }
    },
  }),
  graphql(updateSuitability, { name: 'updateSuitability' }),
  graphql(openCoreInvestmentAccount, { name: 'openCoreInvestmentAccount' }),
  withFormik({
    mapPropsToValues,
    isInitialValid: true,
    validate,
    handleSubmit,
    validateOnChange: false,
  }),
  withProps(
    ({
      responses,
      loading,
      values,
      portfolioId,
      route,
      env,
      isDynamicFunnelEnabled,
      routeToPage,
    }) => {
      if (!loading) {
        const resumeEnabled = env.get('resumeEnabled') === 'true'
        if (portfolioId && resumeEnabled) {
          isDynamicFunnelEnabled
            ? routeToPage(PageName.SuitabilityQuestions, ActionType.Continue)
            : route({
                from: FlowRoute.SuitabilityQuestions,
                action: 'continue',
                doNotTrackVisited: true,
              })
        }

        /* istanbul ignore next */
        return mapRowsAndQuestions(responses as SuitabilityResponse[], values)
      } else {
        return {
          questions: null,
          rows: null,
        }
      }
    },
  ),
  withHandlers({
    handleSelectChange: ({ setFieldValue, submitCount, validateForm }) => async (field, value) => {
      await setFieldValue(field, value)

      // Only validate the form if the user has attempted at least one submit
      /* istanbul ignore next */
      if (submitCount > 0) {
        validateForm()
      }
    },
    onChange: ({ handleChange, submitCount, validateForm }) => async e => {
      // https://github.com/jaredpalmer/formik/issues/1218#issuecomment-451119016
      /* istanbul ignore next */
      await handleChange(e)

      // Only validate the form if the user has attempted at least one submit
      /* istanbul ignore next */
      if (submitCount > 0) {
        validateForm()
      }
    },
  }),
  branch<GqlProps>(({ loading }) => loading, renderComponent(CenteredSpinner)),
)

const FormRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    align-items: stretch;
  }
`

const FormItem = styled.div`
  padding-top: 20px;
`

const StyledSelectInputV3 = styled(SelectInputV3)`
  width: 340px;

  @media screen and (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    width: 100%;
  }
`

export const StyledHeroHeader = styled.div`
  font-size: 22px;
  line-height 30px;
  margin: 30px auto 10px;
  text-align: center;
  max-width: 340px;

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    display: none;
  }
`

export const StyledHeroSubtext = styled.div`
  font-size: 14px;
  line-height: 22px;
  text-align: center;
  margin: 0 auto 20px;
  max-width: 340px;

  p {
    margin: 0;
  }

  @media (max-width: ${style(theme => theme.breakpoints.sm)}px) {
    max-width: 100%;
    margin-bottom: 10px;
  }
`

export const SuitabilityQuestions: React.FC<InnerProps> = props => {
  const {
    heroText,
    isValid,
    loading,
    rows,
    errors,
    handleSelectChange,
    submitForm,
    isSubmitting,
    values,
    isDynamicFunnelEnabled,
    isBackEnabled,
  } = props

  const shouldGrayOutCTA = shouldGrayOutCTACall({
    values,
    validation: v => validate(v, props),
    valuesStartEmpty: true,
  })

  return (
    <>
      <Nav back={isDynamicFunnelEnabled && isBackEnabled(PageName.SuitabilityQuestions)} />
      <Layout header={heroText.header} text={heroText.text}>
        <StyledHeroHeader>{heroText.text}</StyledHeroHeader>
        <StyledHeroSubtext>{heroText.subtext}</StyledHeroSubtext>
        {loading ? (
          <Spinner />
        ) : (
          <>
            <FormContainer>
              <FormRow>
                {rows.map(innerRows =>
                  innerRows.map(row => (
                    <FormItem className="security-question-container" key={row.name}>
                      <StyledSelectInputV3
                        className="select-input"
                        name={row.name}
                        id={row.name}
                        label={row.label}
                        // https://github.com/JedWatson/react-select/issues/1043
                        value={
                          Object.keys(values).length === 0 && values.constructor === Object
                            ? ''
                            : values[row.name]
                        }
                        onChange={(field, value) => handleSelectChange(field, value)}
                        placeholder="Choose one"
                        options={row.options}
                        useNative={false}
                        error={!!errors[row.name]}
                        errorText={errors[row.name] as string}
                      />
                    </FormItem>
                  )),
                )}
              </FormRow>
            </FormContainer>
            <Separator mobileCtaSeparator extraCtaMargin />

            <CTAContainer>
              <SuitabilityQuestionsButton
                styleType={!isValid || shouldGrayOutCTA ? 'invalid' : undefined}
                onPress={submitForm}
                data-testid={TestIDs.SUITABILITY_QUESTIONS.SUBMIT_CTA}
                disabled={isSubmitting}
                buttonType={'submit'}
              >
                {isSubmitting ? <Spinner height={20} width={20} white={true} /> : 'Next'}
              </SuitabilityQuestionsButton>
            </CTAContainer>
          </>
        )}
      </Layout>
    </>
  )
}

export default enhance(SuitabilityQuestions)
